import React, { useState, useEffect, useCallback } from 'react';
import { format, subDays, parseISO } from 'date-fns';
import ReportLayout, { ReportTable, usePagination } from './ReportLayout';
import { ReportChartRow, BarCard, DonutCard, groupCount } from './ReportCharts';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { fmtTime12 } from '../../utils/time';

const fmtDate = v => { try { return v ? format(parseISO(v), 'd MMM yyyy') : '–'; } catch { return v||'–'; } };
const fmtTime = v => { try { return v ? format(parseISO(v), 'h:mm a') : '–'; } catch { return v ? fmtTime12(v) : '–'; } };

const LOG_STATUS_COLOR = {
  administered: 'bg-green-50 text-green-700',
  missed:       'bg-red-50 text-red-600',
  refused:      'bg-amber-50 text-amber-700',
  pending:      'bg-slate-100 text-slate-500',
};

const COLS = [
  { key:'client_name',  label:'Client' },
  { key:'medication',   label:'Medication' },
  { key:'log_date',     label:'Date',         render: fmtDate },
  { key:'scheduled_time',label:'Scheduled',   render: fmtTime },
  { key:'log_status',   label:'Status',       render: v => <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium capitalize', LOG_STATUS_COLOR[v]||'bg-slate-100 text-slate-600')}>{v||'–'}</span> },
  { key:'logged_by',    label:'Logged By' },
  { key:'notes',        label:'Notes',        render: v => v ? <span className="text-xs text-slate-500 max-w-[200px] truncate block">{v}</span> : '–' },
];

const PERIOD_OPTIONS = [
  { label:'Day',   value:'day',   days:1  },
  { label:'Week',  value:'week',  days:7  },
  { label:'Month', value:'month', days:30 },
];

export default function MedicationAdherence() {
  const [data, setData]         = useState([]);
  const [clients, setClients]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [period, setPeriod]     = useState('week');
  const [startDate, setStart]   = useState(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [endDate, setEnd]       = useState(format(new Date(), 'yyyy-MM-dd'));
  const [clientId, setClientId] = useState('');
  const pagination = usePagination(data);

  useEffect(() => {
    api.get('/clients').then(r => setClients(Array.isArray(r.data) ? r.data : r.data?.clients||[])).catch(()=>{});
  }, []);

  const handlePeriodChange = (p) => {
    const opt = PERIOD_OPTIONS.find(o => o.value === p);
    if (opt) {
      setPeriod(p);
      setStart(format(subDays(new Date(), opt.days), 'yyyy-MM-dd'));
      setEnd(format(new Date(), 'yyyy-MM-dd'));
    }
  };

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ start_date: startDate, end_date: endDate });
    if (clientId) p.set('client_id', clientId);
    api.get(`/reports/medication-adherence?${p}`)
      .then(r => { setData(r.data||[]); pagination.reset(); })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [startDate, endDate, clientId]);

  useEffect(() => { load(); }, [load]);

  const selClient = clients.find(c => c.id === clientId);
  const chips = [
    { label:'Period', value:`${startDate} - ${endDate}` },
    ...(selClient ? [{ label:'Client', value:`${selClient.first_name} ${selClient.last_name}`, onRemove: () => setClientId('') }] : []),
  ];

  const administered = data.filter(d => d.log_status === 'administered').length;
  const missed       = data.filter(d => d.log_status === 'missed').length;
  const adherencePct = data.length > 0 ? Math.round(administered / data.length * 100) : null;

  return (
    <ReportLayout title="Medication Adherence Report" data={data} filename="medication_adherence"
      columns={COLS} activeChips={chips} showFiltersPanel={showFilters} onToggleFilters={() => setShowFilters(v=>!v)}
      chart={
        <ReportChartRow>
          <DonutCard title="Adherence breakdown" data={groupCount(data, 'log_status')} />
          <BarCard title="Most missed / refused (client)" data={groupCount(data.filter(r => r.log_status === 'missed' || r.log_status === 'refused'), 'client_name', { limit: 8 })} color="#EF4444" horizontal />
        </ReportChartRow>
      }
      filters={
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Period</label>
            <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
              {PERIOD_OPTIONS.map(o => (
                <button key={o.value} onClick={() => handlePeriodChange(o.value)}
                  className={clsx('px-3 py-1 text-xs font-medium rounded-md transition-all',
                    period === o.value ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700')}>
                  {o.label}
                </button>
              ))}
            </div>
          </div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Start Date</label>
            <input type="date" value={startDate} onChange={e => setStart(e.target.value)} className="input-field !w-auto text-sm"/></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">End Date</label>
            <input type="date" value={endDate} onChange={e => setEnd(e.target.value)} className="input-field !w-auto text-sm"/></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Client</label>
            <select value={clientId} onChange={e => setClientId(e.target.value)} className="input-field !w-auto text-sm">
              <option value="">All Clients</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
            </select></div>
          <div className="flex items-end"><button onClick={load} className="btn-primary text-sm px-4 py-2">Apply</button></div>
        </div>
      }>

      {/* Summary */}
      {data.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{administered}</p>
            <p className="text-xs text-slate-500 mt-0.5">Administered</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-4 text-center">
            <p className="text-2xl font-bold text-red-500">{missed}</p>
            <p className="text-xs text-slate-500 mt-0.5">Missed</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-4 text-center">
            <p className={clsx('text-2xl font-bold', adherencePct >= 80 ? 'text-green-600' : adherencePct >= 60 ? 'text-amber-600' : 'text-red-500')}>
              {adherencePct}%
            </p>
            <p className="text-xs text-slate-500 mt-0.5">Adherence Rate</p>
          </div>
        </div>
      )}

      <ReportTable columns={COLS} data={data} loading={loading} pagination={pagination}/>
    </ReportLayout>
  );
}
